import React, { useState, useEffect } from 'react';
import { X, MessageCircle, ChevronRight, Bot, Briefcase, Code, GraduationCap, Mail, Image } from 'lucide-react';
import { personalInfo } from '../data/portfolioData';

const C = {
  cyan: '#00f5ff', green: '#00ff88', purple: '#7b2fff',
  bg: '#020409', bg2: '#060c14',
  gray: '#4a6275', white: '#e8f4f8',
  border: 'rgba(0,245,255,0.12)',
};

const quickLinks = [
  { icon: Bot,           label: 'AI & ML Work',   sub: 'See my AI projects',       href: '#projects',   filter: 'Machine Learning' },
  { icon: Code,          label: 'Web Projects',    sub: 'Browse web apps I built',  href: '#projects',   filter: 'Web Development' },
  { icon: Briefcase,     label: 'Experience',      sub: 'My work history',          href: '#experience' },
  { icon: GraduationCap, label: 'Education',       sub: 'Academic background',      href: '#education' },
  { icon: Image,         label: 'Gallery',         sub: 'Behind the scenes',        href: '#collage' },
  { icon: Mail,          label: 'Hire Me',         sub: "Let's work together",      href: '#contact',    highlight: true },
];

export default function ChatBubble() {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [hovBtn, setHovBtn] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setVisible(true);
      // Auto-open briefly after delay
      const t2 = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t2);
    }, 2000);
    return () => clearTimeout(t);
  }, []);

  const handleLink = (href) => {
    setOpen(false);
    setTimeout(() => {
      document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    }, 200);
  };

  return (
    <div style={{
      position: 'fixed', bottom: 28, right: 28, zIndex: 9000,
      display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 14,
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(24px)',
      transition: 'opacity .5s ease, transform .5s cubic-bezier(.34,1.56,.64,1)',
    }}>

      {/* Panel */}
      {open && (
        <div style={{
          width: 300,
          background: 'rgba(4,8,16,0.97)',
          border: `1px solid ${C.border}`,
          backdropFilter: 'blur(24px)',
          overflow: 'hidden',
          boxShadow: '0 24px 70px rgba(0,0,0,0.6), 0 0 40px rgba(0,245,255,0.06)',
          animation: 'panelIn .35s cubic-bezier(.34,1.56,.64,1) both',
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #060c14, #0a1628)',
            padding: '16px 18px',
            display: 'flex', alignItems: 'center', gap: 12,
            borderBottom: `1px solid ${C.border}`,
            position: 'relative',
          }}>
            <button
              onClick={() => setOpen(false)}
              style={{
                position: 'absolute', top: 10, right: 10,
                width: 26, height: 26, borderRadius: '50%', border: 'none',
                background: 'rgba(255,255,255,0.07)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: C.white, fontSize: 12, transition: 'background .2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.16)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
            >
              <X size={12} />
            </button>

            {/* Avatar */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              {/* <div style={{
                width: 42, height: 42, borderRadius: '50%',
                background: `linear-gradient(135deg, ${C.cyan}, ${C.purple})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18,
                boxShadow: `0 0 20px rgba(0,245,255,0.3)`,
              }}>
                🤖
              </div> */}
              <div style={{
                width: 42, height: 42, borderRadius: '50%',
                overflow: 'hidden',
                border: `2px solid ${C.cyan}`,
                boxShadow: `0 0 20px rgba(0,245,255,0.3)`,
              }}>
                <img
                  src="/images/foto (2).JPEG"
                  alt="Marshall"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }}
                />
              </div>

              <div style={{
                position: 'absolute', bottom: 1, right: 1,
                width: 10, height: 10, borderRadius: '50%',
                background: C.green, border: `2px solid #060c14`,
                boxShadow: `0 0 6px ${C.green}`,
              }} />
            </div>

            <div>
              <div style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: 13, fontWeight: 700, color: C.white, letterSpacing: 1,
              }}>
                {personalInfo.name.split(' ')[1]}
              </div>
              <div style={{
                fontFamily: "'Space Mono', monospace",
                fontSize: 9, color: 'rgba(232,244,248,0.45)',
                letterSpacing: 1.5, marginTop: 2, textTransform: 'uppercase',
              }}>
                AI Engineer · Bali 🌴
              </div>
            </div>
          </div>

          {/* Greeting */}
          <div style={{ padding: '16px 16px 8px' }}>
            <div style={{
              background: 'rgba(0,245,255,0.04)',
              border: `1px solid ${C.border}`,
              padding: '12px 14px',
              fontSize: 13, color: 'rgba(232,244,248,0.75)', lineHeight: 1.65,
              borderRadius: '2px 12px 12px 12px',
            }}>
              👋 Hey! I'm <strong style={{ color: C.cyan }}>Marshall</strong> — AI Engineer & Web Developer.
              What would you like to explore?
            </div>
            <div style={{
              fontFamily: "'Space Mono', monospace",
              fontSize: 9, color: C.gray,
              marginTop: 6, paddingLeft: 4, letterSpacing: 1,
            }}>
              Usually responds instantly
            </div>
          </div>

          {/* Quick links */}
          <div style={{ padding: '6px 10px 14px', display: 'flex', flexDirection: 'column', gap: 5 }}>
            {quickLinks.map((link, i) => {
              const Icon = link.icon;
              const isHov = hovered === i;
              return (
                <button key={i}
                  onClick={() => handleLink(link.href)}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    width: '100%', padding: '9px 12px',
                    border: `1px solid ${isHov || link.highlight ? (link.highlight ? C.cyan : 'rgba(0,245,255,0.3)') : C.border}`,
                    cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                    background: link.highlight
                      ? (isHov ? 'rgba(0,245,255,0.18)' : 'rgba(0,245,255,0.08)')
                      : (isHov ? 'rgba(0,245,255,0.04)' : 'transparent'),
                    color: C.white,
                    transition: 'all .25s cubic-bezier(.34,1.56,.64,1)',
                    transform: isHov ? 'translateX(3px)' : 'none',
                  }}
                >
                  <div style={{
                    width: 30, height: 30, flexShrink: 0,
                    background: isHov || link.highlight ? 'rgba(0,245,255,0.1)' : 'rgba(0,245,255,0.04)',
                    border: `1px solid ${C.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all .25s',
                  }}>
                    <Icon size={13} color={link.highlight || isHov ? C.cyan : C.gray} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: 12.5, fontWeight: 600,
                      color: link.highlight ? C.cyan : C.white,
                    }}>
                      {link.label}
                    </div>
                    <div style={{ fontSize: 10, color: C.gray, marginTop: 1 }}>
                      {link.sub}
                    </div>
                  </div>
                  <ChevronRight size={12} color={isHov ? C.cyan : C.gray}
                    style={{ transition: 'transform .2s', transform: isHov ? 'translateX(2px)' : 'none', flexShrink: 0 }}
                  />
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{
            padding: '8px 16px 12px',
            borderTop: `1px solid ${C.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 4, height: 4, borderRadius: '50%', background: C.cyan,
                animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
              }} />
            ))}
            <span style={{
              fontFamily: "'Space Mono', monospace",
              fontSize: 9, color: C.gray, letterSpacing: 1.5, textTransform: 'uppercase',
            }}>
              Portfolio v1.0
            </span>
          </div>
        </div>
      )}

      {/* Toggle Button */}
      <button
        onClick={() => setOpen(v => !v)}
        onMouseEnter={() => setHovBtn(true)}
        onMouseLeave={() => setHovBtn(false)}
        style={{
          width: 54, height: 54, borderRadius: '50%', border: `1px solid ${C.border}`,
          cursor: 'pointer',
          background: open
            ? 'linear-gradient(135deg, #0a1628, #111827)'
            : `linear-gradient(135deg, ${C.cyan}, #00c4cc)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: open
            ? '0 8px 24px rgba(0,0,0,0.4)'
            : (hovBtn ? '0 12px 32px rgba(0,245,255,0.5)' : '0 6px 22px rgba(0,245,255,0.35)'),
          transform: hovBtn ? 'scale(1.12) translateY(-2px)' : 'scale(1)',
          animation: (!open && !hovBtn) ? 'chatPulse 3s ease-in-out infinite' : 'none',
          transition: 'all .35s cubic-bezier(.34,1.56,.64,1)',
        }}
        aria-label={open ? 'Close menu' : 'Open navigation'}
      >
        <div style={{
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform .4s cubic-bezier(.34,1.56,.64,1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {open
            ? <X size={20} color={C.white} />
            : <MessageCircle size={20} color="#020409" strokeWidth={2.5} />
          }
        </div>
      </button>
    </div>
  );
}
